import { useUserContext } from "../../Hooks/useUserContext";
import { useUnitAppropriationService } from "../../Hooks/useUnitAppropriationService";
import { RouterLink } from "../../Shared/RouterLink";
import { AuthLoadingScreen } from "../../Shared/AuthLoadingScreen";
import { parseRelaxedJson } from "../../Helpers/jsonHelper";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button,
	IconButton,
	Tooltip,
	Paper,
	TextField,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import { Spinner } from "../../Shared/Spinner";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ImportIcon from "@material-ui/icons/GetApp";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import green from "@material-ui/core/colors/green";

export function UnitAppropriationAdmin(props) {
	const user = useUserContext(props.config);
	const service = useUnitAppropriationService(props.config);
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [deletingId, setDeletingId] = useState(null);
	const [importText, setImportText] = useState("");
	const [showImport, setShowImport] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [toastMessage, setToastMessage] = useState("");

	const hasPermission = user?.isUnitAppropriationAdmin ?? false;

	const sortedItems = [...items].sort((a, b) => {
		const aInput = (a?.inputUnit ?? "").toString().toLocaleLowerCase();
		const bInput = (b?.inputUnit ?? "").toString().toLocaleLowerCase();
		if (aInput < bInput) return -1;
		if (aInput > bInput) return 1;
		return 0;
	});

	useEffect(() => {
		document.title = "Unit Appropriations - " + props.config.appName;
		if (!hasPermission)
			return;

		loadItems();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function loadItems() {
		setIsLoading(true);
		service.getAll(
			(response) => {
				if (response && response.status === 200 && response.data) {
					setItems(response.data);
				}
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				alert("Failed to load unit appropriations");
				setIsLoading(false);
			}
		);
	}

	function handleDelete(event, item) {
		if (event && event.preventDefault)
			event.preventDefault();

		if (!window.confirm(`Delete "${item.inputUnit} → ${item.outputUnit}"?`))
			return;

		setDeletingId(item.unitAppropriationId);
		service.deleteItem(item.unitAppropriationId, () => {
			loadItems();
			setDeletingId(null);
		}, (error) => {
			console.error(error);
			alert("Failed to delete unit appropriation");
			setDeletingId(null);
		});
	}

	function handleImport() {
		try {
			const parsed = parseRelaxedJson(importText);
			const appropriations = parsed.map(item => ({
				inputUnit: item.InputUnit,
				rules: (item.Rules || []).join("|"),
				conversionEquation: item.ConversionEquation,
				outputUnit: item.OutputUnit,
				notes: item.Notes || ""
			}));

			service.importItems(appropriations, (response) => {
				if (response && response.status === 200) {
					setItems(response.data);
					setShowImport(false);
					setImportText("");
					setToastMessage("Import successful!");
					setToastOpen(true);
				} else {
					alert("Failed to import");
				}
			}, (error) => {
				console.error(error);
				alert("Failed to import: " + (error.response?.data || error.message));
			});
		} catch (e) {
			alert("Invalid JSON format. Expected array of objects with InputUnit, Rules (array), ConversionEquation, OutputUnit, and optional Notes properties.");
		}
	}

	function handleExport() {
		const exportData = items.map(item => ({
			InputUnit: item.inputUnit,
			Rules: item.rules ? item.rules.split('|').filter(r => r) : [],
			ConversionEquation: item.conversionEquation,
			OutputUnit: item.outputUnit,
			Notes: item.notes ?? ""
		}));

		const jsonString = JSON.stringify(exportData, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
		link.download = `unit-appropriations-${timestamp}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setTimeout(() => URL.revokeObjectURL(url), 100);
		setToastMessage("Export successful!");
		setToastOpen(true);
	}

	if (!user.isInitialized) {
		return <AuthLoadingScreen />;
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have unit appropriation admin permissions to view this page!
					</Typography>
					<Typography style={{ paddingLeft: 40 }} variant="subtitle1">
						Logged in: {user.isLoggedIn ? "True" : "False"}
					</Typography>
					<Typography style={{ paddingTop: 20 }} variant="subtitle1">
						<RouterLink to="/login" >
							{user.isLoggedIn ? "" : "Login Now"}
						</RouterLink>
					</Typography>
				</Grid>
			</Grid>
		);

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					Unit Appropriations
				</Typography>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: 12 }}>
				<Button
					variant="contained"
					color="primary"
					component={RouterLink}
					to="/admin/unit-appropriations/create"
				>
					<AddIcon style={{ marginRight: 10 }} />
					Add Appropriation
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setShowImport(!showImport)}
					style={{ marginLeft: 12 }}
				>
					<ImportIcon style={{ marginRight: 8 }} />
					Import
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handleExport}
					style={{ marginLeft: 12 }}
				>
					<SaveAltIcon style={{ marginRight: 8 }} />
					Export
				</Button>
			</Grid>
			{showImport && (
				<Grid item xs={12}>
					<Paper style={{ padding: 20, marginBottom: 20 }}>
						<Typography variant="h6" gutterBottom>
							Import from Config Format
						</Typography>
						<Typography variant="body2" gutterBottom>
							Paste the UnitAppropriations JSON array from your config file:
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={8}
							variant="outlined"
							value={importText}
							onChange={(e) => setImportText(e.target.value)}
							placeholder={'[\n  { "InputUnit": "oz", "Rules": ["{{value}} >= 16"], "ConversionEquation": "{{value}} / 16", "OutputUnit": "lbs" },\n  ...\n]'}
							style={{ marginBottom: 10 }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={handleImport}
							style={{ marginRight: 10 }}
						>
							Import
						</Button>
						<Button
							variant="outlined"
							onClick={() => { setShowImport(false); setImportText(""); }}
						>
							Cancel
						</Button>
					</Paper>
				</Grid>
			)}
			<Grid item xs={13}>{ /* +1 more to fix formatting issues */}
				<Paper>
					{isLoading ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
							<Spinner size={24} style={{ marginRight: 10 }} />
							<Typography>Loading...</Typography>
						</div>
					) : (
						<Table className="nowrap-table condensed-table">
							<TableHead>
								<TableRow className="user-admin-header">
									<TableCell style={{ width: 1, padding: 4 }}></TableCell>
									<TableCell>Input Unit</TableCell>
									<TableCell>Rules</TableCell>
									<TableCell>Conversion Equation</TableCell>
									<TableCell>Output Unit</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedItems.map((item) => (
									<TableRow key={item.unitAppropriationId}>
										<TableCell align="left" style={{ width: 1, padding: 4 }}>
											<div style={{ display: "flex", gap: 4 }}>
												<Tooltip title="Edit">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														component={RouterLink}
														to={`/admin/unit-appropriations/${item.unitAppropriationId}/edit`}
														aria-label="Edit unit appropriation"
													>
														<EditIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														onClick={(e) => handleDelete(e, item)}
														disabled={deletingId === item.unitAppropriationId}
														aria-label="Delete unit appropriation"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
										<TableCell>{item.inputUnit}</TableCell>
										<TableCell>{item.rules ? item.rules.split('|').join(', ') : ''}</TableCell>
										<TableCell>{item.conversionEquation}</TableCell>
										<TableCell>{item.outputUnit}</TableCell>
										<TableCell>{item.notes}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>{toastMessage}</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
